import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { getSessionToken, setSessionToken } from "../lib/sessionToken";
import api from "../lib/api";
import { isNative, platform } from "../lib/native";
import { startIapRecovery } from "../lib/iap";
import { startGoogleBillingEvents } from "../lib/googleBillingEvents";
import i18n from "../i18n";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Sur le web, l'authentification repose uniquement sur le cookie HttpOnly :
// on ne stocke JAMAIS le token en localStorage (protection anti-XSS). Sur
// l'app native, le cookie cross-site n'est pas fiable -> on conserve le token.
const storeToken = (t) => {
  if (isNative() && t) setSessionToken(t); // C10 : Keychain / Keystore
};

// Un compte valide est toujours un objet avec user_id. Toute autre réponse (page HTML renvoyée par
// le conteneur natif quand l'adresse de l'API est absente, ancien format, proxy…) est refusée :
// sans cette garde, user devenait undefined et l'application entière plantait (écran noir sur iPhone).
export const isAccount = (u) => !!u && typeof u === "object" && typeof u.user_id === "string" && !!u.user_id;
const sessionUser = (data) => {
  const u = data && typeof data === "object" ? data.user : null;
  if (!isAccount(u)) throw new Error(i18n.t("common.generic_error"));
  return u;
};

export function AuthProvider({ children }) {
  const [billingState, setBillingState] = useState({});
  const [user, updateUser] = useState(null); // null=loading, false=anon, object=auth
  const [subscriptionError, setSubscriptionError] = useState("");
  const [billingBusy, setBillingBusy] = useState(false);
  const actionRunning = useRef(false);
  const generation = useRef(0);
  const requestId = useRef(0);
  const setUser = useCallback((value) => {
    generation.current++;
    requestId.current++;
    setSubscriptionError("");
    updateUser(value);
  }, []);

  const checkAuth = useCallback(async () => {
    const epoch = generation.current;
    try {
      const { data } = await api.get("/auth/me");
      if (!isAccount(data)) throw new Error("invalid session");
      if (epoch === generation.current) setUser(data);
    } catch {
      if (epoch !== generation.current) return;
      setSessionToken(null);
      setUser(false);
    }
  }, [setUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, totpCode) => {
    const { data } = await api.post("/auth/login", { email, password, ...(totpCode ? { totp_code: totpCode } : {}) });
    const account = sessionUser(data);
    storeToken(data.token);
    setUser(account);
    return account;
  };

  const register = async (name, email, password, partnerCode, acceptTerms) => {
    // Code partenaire : champ app (facultatif) ou Play Install Referrer capté
    // au 1er lancement. L'attribution reste définitive côté backend.
    const code = partnerCode || (() => { try { return localStorage.getItem("hitmax_partner_referrer"); } catch { return null; } })();
    const { data } = await api.post("/auth/register", {
      name, email, password, origin_url: window.location.origin,
      partner_code: code || null, partner_source: partnerCode ? "manuel" : (code ? "referrer" : null),
      accept_terms: !!acceptTerms,
    });
    try { localStorage.removeItem("hitmax_partner_referrer"); } catch {}
    // C16 : le compte s'ouvre après confirmation de l'adresse (lien reçu par e-mail).
    if (data && typeof data === "object" && data.pending_verification) return { pending: true, message: data.message };
    const account = sessionUser(data);
    storeToken(data.token);
    setUser(account);
    return account;
  };

  // Sign in with Apple NATIF (app iOS) : fenêtre système d'Apple, puis échange du jeton d'identité.
  const loginWithAppleNative = async () => {
    const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
    // Bind Apple's signed identity to this attempt. The backend receives the
    // raw nonce; it must compare its SHA-256 with the nonce claim signed by Apple.
    const bytes = window.crypto.getRandomValues(new Uint8Array(32));
    const nonce = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(nonce));
    const hashedNonce = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    const { response } = await SignInWithApple.authorize({
      clientId: "io.hitmax.app",
      redirectURI: `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/callback`,
      scopes: "email name",
      nonce: hashedNonce,
    });
    const { data } = await api.post("/auth/apple/native", {
      identity_token: response.identityToken,
      authorization_code: response.authorizationCode || null,
      nonce,
      given_name: response.givenName || null,
      family_name: response.familyName || null,
    });
    const account = sessionUser(data);
    storeToken(data.token);
    setUser(account);
    return account;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    const account = sessionUser(data);
    storeToken(data.token);
    setUser(account);
    return account;
  };

  const logout = async () => {
    setUser(false);
    const session = getSessionToken();
    try { await api.post("/auth/logout"); } catch {}
    if (getSessionToken() === session) {
      setSessionToken(null);
    }
  };

  const refreshUser = useCallback(async () => {
    const epoch = generation.current;
    const id = ++requestId.current;
    const session = getSessionToken();
    const current = () => epoch === generation.current && getSessionToken() === session;
    try {
      const { data } = await api.get("/auth/me");
      if (!isAccount(data)) throw new Error("invalid session");
      if (!current()) throw new Error("Le compte a changé. Réessaie avec le compte connecté.");
      if (id === requestId.current) {
        updateUser(data);
        setSubscriptionError("");
      }
      return data;
    } catch (error) {
      if (current()) {
        const message = "Impossible d’actualiser ton abonnement. Les informations affichées peuvent être anciennes.";
        if (id === requestId.current) setSubscriptionError(message);
        throw new Error(message, { cause: error });
      }
      throw error;
    }
  }, []);

  const runBillingAction = useCallback(async (work) => {
    if (actionRunning.current) throw new Error("Une opération d’achat ou de restauration est déjà en cours.");
    actionRunning.current = true;
    const epoch = generation.current;
    setBillingBusy(true);
    try {
      const result = await work();
      if (generation.current !== epoch) throw new Error("Le compte a changé. Vérifie tes achats avec le compte concerné.");
      return result;
    }
    finally { actionRunning.current = false; setBillingBusy(false); }
  }, []);

  const accountId = user?.user_id;
  const subscriptionMissing = !!accountId && !user?.subscription;
  const nativeSession = isNative() ? getSessionToken() : null;
  useEffect(() => {
    setBillingState({});
    if (!accountId || !isNative()) return;
    let stopped = false;
    let cleanup = () => {};
    const changed = () => { if (!stopped) refreshUser().catch(() => {}); };
    window.addEventListener("hitmax-iap-delivered", changed);
    startIapRecovery((state) => { if (!stopped) setBillingState(state); })
      .then((stop) => { if (stopped) stop(); else cleanup = stop; })
      .catch(() => { if (!stopped) setBillingState({ problem: "Reprise des achats indisponible. Utilise Restaurer mes achats." }); });
    return () => {
      stopped = true;
      cleanup();
      window.removeEventListener("hitmax-iap-delivered", changed);
    };
  }, [accountId, nativeSession, refreshUser]);

  useEffect(() => {
    if (!accountId) return;
    let stopped = false;
    let handle;
    const refresh = () => { if (!stopped) refreshUser().catch(() => {}); };
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    const events = isNative() && platform() === "android" && nativeSession
      ? startGoogleBillingEvents({ session: nativeSession, refresh: refreshUser }) : null;
    refresh();
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", visible);
    if (isNative()) import("@capacitor/app").then(async ({ App }) => {
      const listener = await App.addListener("appStateChange", ({ isActive }) => {
        events?.foreground(isActive);
        if (isActive) refresh();
      });
      if (stopped) listener.remove(); else handle = listener;
    }).catch(() => {});
    return () => {
      stopped = true;
      events?.stop();
      handle?.remove();
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [accountId, nativeSession, refreshUser, subscriptionMissing]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, loginWithGoogleSession, loginWithAppleNative, logout, refreshUser, billingState, subscription: user?.subscription || null, subscriptionError, billingBusy, runBillingAction }}>
      {children}
    </AuthContext.Provider>
  );
}
